/**
 * Format seconds into human-readable uptime string.
 */
export function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}д`);
  if (hours > 0) parts.push(`${hours}ч`);
  if (minutes > 0) parts.push(`${minutes}м`);
  if (secs > 0 && days === 0) parts.push(`${secs}с`);

  return parts.join(' ') || '0с';
}

/**
 * Format hashrate with appropriate units.
 */
export function formatHashrate(hashrate: number): { value: string; unit: string } {
  if (hashrate >= 1_000_000) {
    return { value: (hashrate / 1_000_000).toFixed(2), unit: 'GH/s' };
  }
  if (hashrate >= 1_000) {
    return { value: (hashrate / 1_000).toFixed(2), unit: 'MH/s' };
  }
  return { value: hashrate.toFixed(2), unit: 'H/s' };
}

/**
 * Format power consumption.
 */
export function formatPower(watts: number): { value: string; unit: string } {
  if (watts >= 1000) {
    return { value: (watts / 1000).toFixed(2), unit: 'kW' };
  }
  return { value: watts.toString(), unit: 'W' };
}

/**
 * Format market cap with abbreviation.
 */
export function formatMarketCap(value: number | null | undefined): string {
  if (value == null) return 'N/A';
  if (value >= 1e12) return (value / 1e12).toFixed(2) + 'T';
  if (value >= 1e9) return (value / 1e9).toFixed(2) + 'B';
  if (value >= 1e6) return (value / 1e6).toFixed(2) + 'M';
  if (value >= 1e3) return (value / 1e3).toFixed(2) + 'K';
  return value.toLocaleString();
}

/**
 * Format price with appropriate decimal places.
 */
export function formatPrice(price: number): string {
  if (price >= 1) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (price >= 0.01) return price.toFixed(4);
  return price.toFixed(6);
}

/**
 * Format relative time ("5 мин назад").
 */
export function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'только что';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} мин назад`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} ч назад`;
  return `${Math.floor(seconds / 86400)} д назад`;
}

/**
 * Format coin amount from satoshis (1e8).
 */
export function formatCoinAmount(satoshis: number, decimals = 5): string {
  return (satoshis / 1e8).toFixed(decimals);
}

/**
 * Get temperature CSS class based on value and threshold.
 */
export function tempClass(temp: number, warnAt: number, criticalAt: number): string {
  if (temp >= criticalAt) return 'temp-critical';
  if (temp >= warnAt) return 'temp-hot';
  if (temp >= warnAt - 10) return 'temp-warm';
  return 'temp-normal';
}

/**
 * Get color for temperature bar fill.
 */
export function tempColor(temp: number, warnAt: number, criticalAt: number): string {
  if (temp >= criticalAt) return 'var(--red)';
  if (temp >= warnAt) return 'var(--red)';
  if (temp >= warnAt - 10) return 'var(--amber)';
  return 'var(--lime)';
}

/**
 * Format timeout display (minutes -> human readable).
 */
export function formatTimeout(minutes: number): string {
  if (minutes >= 1440) return `${minutes / 1440} сут`;
  if (minutes >= 60) return `${minutes / 60} ч`;
  return `${minutes} мин`;
}

/**
 * Generate CSV string from rows.
 */
export function toCSV(headers: string[], rows: string[][]): string {
  const escape = (s: string) => `"${s.replace(/"/g, '""')}"`;
  const lines = [headers.map(escape).join(',')];
  for (const row of rows) {
    lines.push(row.map(escape).join(','));
  }
  return lines.join('\n');
}

/**
 * Trigger browser download of a string as a file.
 */
export function downloadFile(content: string, filename: string, mime = 'text/csv') {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Copy text to clipboard, return true on success.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
